import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import * as Sentry from '@sentry/vue'
import api from '@/api'
import billingApi from '@/api/modules/billingApi'
import type { User } from '@/types/user'
import type { Account } from '@/types/account'
import type { WebappConfig } from '@/api/modules/webappConfig'
import { userLabel } from '@/utils/users'
import { usePlatformCountry } from '@/composables/usePlatformCountry'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user: Ref<User | null> = ref(null)

  // Keep Sentry's user context in sync however `user` changes (login, restore, logout).
  watch(user, (currentUser) => {
    Sentry.setUser(currentUser ? {
      id: currentUser.id,
      email: currentUser.email,
      username: userLabel(currentUser),
    } : null)
  }, { immediate: true })

  const account: Ref<Account | null> = ref(null)
  const { setPlatformCountry } = usePlatformCountry()

  // The signed-in account's platform country, the one access decisions use; null = not set yet.
  const accountPlatformCountry = computed(() => account.value?.platform_country_code ?? null)

  // Once signed in, the active account decides the platform country (rules, data, language);
  // the login page's guess only applies before that.
  const setActiveAccount = async (activeAccount: Account): Promise<void> => {
    account.value = activeAccount
    localStorage.setItem('account', JSON.stringify(activeAccount))
    await setPlatformCountry(activeAccount.platform_country_code)
  }
  const config: Ref<WebappConfig | null> = ref(null)
  // Whether the active account has a live subscription. `null` = not yet known
  // (or the check failed). The router gate only blocks on an explicit `false`,
  // so a billing-endpoint hiccup fails open rather than locking users out.
  const isSubscribed: Ref<boolean | null> = ref(null)
  const myAccounts: Ref<any[]> = ref([])
  const isInitialized: Ref<boolean> = ref(false)
  const isLoading: Ref<boolean> = ref(false)

  // Getters
  const isAuthenticated: ComputedRef<boolean> = computed(() => !!user.value)
  const isSystemAdmin: ComputedRef<boolean> = computed(() => !!user.value && !!user.value.is_system_admin)
  const isAccountAdmin: ComputedRef<boolean> = computed(() => !!user.value && !!user.value.is_account_admin)
  const isCialLogin: ComputedRef<boolean> = computed(() => config.value?.env === 'prod' || config.value?.env === 'production')

  // Actions
  const initializeConfig = async (): Promise<void> => {
    if (config.value) return
    try {
      config.value = await api.webappConfig.getConfig()
    } catch (error) {
      console.error('Failed to initialize webapp config:', error)
      // Fallback to minimal defaults if backend fails
      config.value = {
        cial_auth_url: '',
        cial_client_id: '',
        env: 'unknown',
        is_local: true,
        kyx_free_entities_limit: null,
        kyx_base_token_price: null,
        kyx_excess_pricing_mode: null,
        kyx_per_entity_token_price: null,
        min_topup_tokens: 1000
      }
    }
  }

  const login = async (credentials: { email: string; password: string }): Promise<any> => {
    try {
      isLoading.value = true
      const response = await api.auth.login(credentials)

      if (response.user) {
        user.value = response.user
        localStorage.setItem('user', JSON.stringify(response.user))
      }
      if (response.account) {
        await setActiveAccount(response.account)
      }

      // Fetch user's authorized accounts and subscription status after login
      await Promise.all([getMyAccounts(), refreshSubscription()])

      return response
    } finally {
      isLoading.value = false
    }
  }

  const loginWithCial = async (
    code: string,
    codeVerifier: string,
    nonce: string | null,
    state: string
  ): Promise<any> => {
    try {
      isLoading.value = true
      const response = await api.auth.exchangeCialCode(code, codeVerifier, nonce, state)

      if (response.user) {
        user.value = response.user
        localStorage.setItem('user', JSON.stringify(response.user))
      }
      if (response.account) {
        await setActiveAccount(response.account)
      }

      // Fetch user's authorized accounts and subscription status after login
      await Promise.all([getMyAccounts(), refreshSubscription()])

      return response
    } finally {
      isLoading.value = false
    }
  }

  // Refresh it after any change that could flip it (e.g. the
  // account just subscribed) so a mid-session subscribe resolves without re-login.
  const refreshSubscription = async (): Promise<void> => {
    try {
      const summary = await billingApi.getSummary()
      isSubscribed.value = !!summary.subscription
    } catch (error) {
      console.error('Failed to fetch subscription status:', error)
      // Fail open: don't lock the user out because the billing endpoint hiccuped.
      isSubscribed.value = null
    }
  }

  const getMyAccounts = async (): Promise<void> => {
    try {
      const accounts = await api.auth.getMyAccounts()
      myAccounts.value = accounts
    } catch (error) {
      console.error('Failed to fetch user accounts:', error)
    }
  }

  const switchAccount = async (accountId: string): Promise<void> => {
    try {
      isLoading.value = true
      const response = await api.auth.switchAccount(accountId)

      if (response.account) {
        await setActiveAccount(response.account)

        // Force a page reload to the root to clear all company-specific data from Pinia/Memory
        window.location.assign('/')
      }
    } catch (error) {
      console.error('Failed to switch account:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    try {
      // Always attempt server logout (cookie-based session)
      await api.auth.logout()
    } catch (error) {
      console.error('Logout API error:', error)
    } finally {
      // After logout, force interactive auth in the next CIAL login attempt.
      sessionStorage.setItem('force_cial_reauth', '1')
      // Clear local state regardless of API call success
      clearAuth()
    }
  }

  const changePassword = async (newPassword: string, currentPassword: string): Promise<void> => {
    try {
      isLoading.value = true
      await api.auth.changePassword(newPassword, currentPassword)
    } finally {
      isLoading.value = false
    }
  }

  const clearAuth = (): void => {
    user.value = null
    account.value = null
    myAccounts.value = []
    isSubscribed.value = null
    localStorage.removeItem('user')
    localStorage.removeItem('account')
  }

  const getCurrentUser = async (): Promise<any> => {
    try {
      isLoading.value = true
      const response = await api.auth.getCurrentUser()

      if (response.authenticated === false) {
        clearAuth()
        return response
      }

      if (response.authenticated === true && response.user) {
        user.value = response.user
        localStorage.setItem('user', JSON.stringify(response.user))

        if (response.account) {
          await setActiveAccount(response.account)
        }

        // Automatically populate account list and subscription status if authenticated
        await Promise.all([getMyAccounts(), refreshSubscription()])

        return response
      }

      console.warn('Unexpected response format from /auth/me:', response)
      clearAuth()
      return response
    } catch (error: any) {
      console.error('Error checking authentication status:', error)

      if (error.response?.status === 401) {
        clearAuth()
      } else {
        console.error('Network/server error during auth check:', error)
      }

      throw error
    } finally {
      isLoading.value = false
    }
  }

  const initializeAuth = async (): Promise<void> => {
    if (isInitialized.value) {
      return // Already initialized
    }

    try {
      isLoading.value = true
      // Always fetch config first as it's needed for the UI environment markers and login flow
      await initializeConfig()
      await getCurrentUser()
    } catch (error: any) {
      console.warn('Failed to get current user from backend:', error)

      if (error.response?.status !== 401) {
        const savedUser = localStorage.getItem('user')
        const savedAccount = localStorage.getItem('account')

        if (savedUser) {
          try {
            user.value = JSON.parse(savedUser)
            console.log('Restored user from localStorage due to network/server error')
          } catch (e) {
            console.error('Failed to parse saved user:', e)
            clearAuth()
          }
        }

        if (savedAccount) {
          try {
            account.value = JSON.parse(savedAccount)
          } catch (e) {
            console.error('Failed to parse saved account:', e)
          }
        }
      }
    } finally {
      isInitialized.value = true
      isLoading.value = false
    }
  }

  return {
    // State
    user,
    account,
    accountPlatformCountry,
    config,
    myAccounts,
    isSubscribed,
    isAuthenticated,
    isInitialized,
    isLoading,
    isSystemAdmin,
    isAccountAdmin,
    isCialLogin,

    // Actions
    login,
    loginWithCial,
    getMyAccounts,
    refreshSubscription,
    switchAccount,
    logout,
    changePassword,
    clearAuth,
    getCurrentUser,
    initializeConfig,
    initializeAuth
  }
})
